import posthog from "posthog-js";
import { useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";

import ReferFriendModal from "../../components/Rewards/ReferFriendModal";
import { Button } from "../../components/ui/button";
import { useAuth } from "../../context/Auth/AuthContext";
import {
	collectableRewards,
	completionPctOf,
	findOwnResumeReward,
	type Reward,
	rewardErrorMessage,
	summarisePoints,
} from "../../handlers/rewards";
import { currencyFormat } from "../../handlers/utilityFunctions";
import { useResumes, useUserAccount } from "../../services/create-resume/queries";
import { useClaimReward, usePayments, useRewards } from "../../services/create-resume/rewardQueries";
import Header from "../Profile/components/Header";
import ActivityList from "./components/ActivityList";
import EarnCards from "./components/EarnCards";
import PlanPanel from "./components/PlanPanel";
import PointsPanel from "./components/PointsPanel";

/**
 * Points and the subscription they buy, on one screen.
 *
 * These were two pages behind two different sidebars, and between them they drew eleven visitors in
 * a quarter. They are one subject — a balance is only interesting next to the thing it discounts —
 * so they are now one page, reachable from the profile rail.
 */
function Rewards() {
	const navigate = useNavigate();
	const { isLoggedIn } = useAuth();

	const [showReferModal, setShowReferModal] = useState(false);
	const [collectingIds, setCollectingIds] = useState<string[]>([]);
	const [isScrolled, setIsScrolled] = useState(false);

	const {
		data: rewards = [],
		isLoading: isLoadingRewards,
		isError: rewardsFailed,
		refetch: refetchRewards,
	} = useRewards();
	const { data: account } = useUserAccount();
	const { data: payments = [], isLoading: isLoadingPayments } = usePayments(account?.userID);
	const { data: resumeList } = useResumes();

	useEffect(() => {
		posthog.capture("rewards_viewed");
	}, []);

	// The router has no scroll restoration, so a navigation here from a scrolled page would otherwise
	// open this one part-way down.
	useEffect(() => {
		window.scrollTo(0, 0);
	}, []);

	useEffect(() => {
		const handleScroll = () => setIsScrolled(window.scrollY > 0);
		window.addEventListener("scroll", handleScroll);
		return () => window.removeEventListener("scroll", handleScroll);
	}, []);

	const summary = useMemo(() => summarisePoints(rewards as Reward[]), [rewards]);
	const claimable = useMemo(() => collectableRewards(rewards as Reward[]), [rewards]);
	const ownResumeReward = useMemo(() => findOwnResumeReward(rewards as Reward[]), [rewards]);
	const resumePct = ownResumeReward ? completionPctOf(ownResumeReward) : 0;

	const { mutateAsync: claimReward } = useClaimReward();

	/**
	 * Collect one reward, or every ready one, through the same path.
	 *
	 * Sequential rather than parallel: these all write to the same account, and a run that stops
	 * half-way should report the points that actually landed rather than a total it hoped for. The
	 * refetch runs either way, so a partial collection still leaves the wallet telling the truth.
	 */
	const collect = async (ids: string[]) => {
		if (ids.length === 0 || collectingIds.length > 0) return;
		setCollectingIds(ids);

		let collected = 0;
		try {
			for (const id of ids) {
				const reward = await claimReward(id);
				collected += Number(reward?.rewardPoints) || 0;
			}
			toast.success(`${currencyFormat(collected)} points collected. Spend them on any plan.`);
			posthog.capture("reward_collected", { points: collected, rewards: ids.length });
		} catch (error) {
			toast.error(rewardErrorMessage(error, "We could not collect those points. Please try again."));
			posthog.capture("reward_collect_failed", { collected, of: ids.length });
		} finally {
			setCollectingIds([]);
			refetchRewards();
		}
	};

	/**
	 * The server's own nudge, sent alongside the WhatsApp message rather than instead of it.
	 *
	 * There is no scheduled job behind this — the only thing that emails a referred person is this
	 * endpoint, and it only does so for a referral under the threshold that has not been nudged for a
	 * week. It answers 409 either way, so a failure here is the normal path and says nothing about
	 * whether the email went. The reader already has their outcome in the WhatsApp window.
	 */
	const { mutate: sendServerNudge } = useClaimReward({
		// Reachable only if the server finds a completion the cached row has not caught up with, in
		// which case this collected the points rather than nudging anyone.
		onSuccess: (data: { rewardPoints?: number }) => {
			toast.success(`${data?.rewardPoints ?? ""} points collected. Spend them on any plan.`.trim());
			refetchRewards();
		},
		onError: () => {},
	});

	const goToResume = () => {
		posthog.capture("rewards_build_resume_clicked", { resumePct });
		navigate(Array.isArray(resumeList) && resumeList.length > 0 ? "/my-resumes" : "/resume/select-experience");
	};

	const openReferModal = () => {
		posthog.capture("referral_modal_opened");
		setShowReferModal(true);
	};

	// Points are only ever spent at checkout, where the server applies them against the plan price.
	const goToRedeem = () => {
		posthog.capture("rewards_redeem_clicked", { available: summary.available });
		navigate("/payments");
	};

	if (!isLoggedIn) return <Navigate to="/signin" replace />;

	return (
		<>
			{/* The shadow is what makes content passing underneath read as layered rather than clipped. */}
			<div className={`sticky top-0 z-50 bg-white transition-all duration-300 ${isScrolled ? "shadow-xs" : ""}`}>
				<Header variant="applicant" />
			</div>

			<ReferFriendModal open={showReferModal} onClose={() => setShowReferModal(false)} />

			<div className="min-h-screen bg-gray-50">
				<div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
					<h1 className="mb-8 text-3xl font-bold text-gray-900 font-plus-jakarta">Rewards</h1>

					{isLoadingRewards ? (
						<div className="rounded-lg border border-gray-200 bg-white p-10 text-center text-sm text-gray-500">
							Loading your points...
						</div>
					) : rewardsFailed ? (
						/* Without this the page renders a confident "0 points" for what is really a failed
						   read, telling someone who has a balance that they have nothing. */
						<div className="rounded-lg border border-gray-200 bg-white p-10 text-center">
							<p className="text-sm text-gray-600 font-inter">We could not load your points just now.</p>
							<Button variant="outlineSecondary" className="mt-4" onClick={() => refetchRewards()}>
								Try again
							</Button>
						</div>
					) : (
						/* Source order is the phone order: the balance is what someone came to see, so it
						   leads, and the rail follows rather than being reordered around it. */
						<div className="flex flex-col lg:flex-row gap-8">
							<div className="lg:w-2/3 space-y-8">
								<PointsPanel
									summary={summary}
									claimablePoints={claimable.points}
									collecting={collectingIds.length > 0}
									onCollect={() => collect(claimable.ids)}
									onRefer={openReferModal}
									onRedeem={goToRedeem}
								/>

								{/* A phone stacks the rail under the table, which puts the only two ways to earn
								    below every row someone has ever earned. Here they sit against the balance
								    they change, and the rail keeps the copy that belongs beside the plan. */}
								<div className="lg:hidden">
									<EarnCards resumePct={resumePct} onBuildResume={goToResume} onRefer={openReferModal} />
								</div>

								<ActivityList
									rewards={rewards as Reward[]}
									accountEmail={account?.email}
									collectingIds={collectingIds}
									onRedeem={goToRedeem}
									onCollect={(reward) => collect([reward._id])}
									/* The row opens WhatsApp itself, so there is nothing to wait for here. The
									   server nudge rides along: it throttles itself to one email a week and ignores
									   the call otherwise, so it cannot gate the message the reader is already sending. */
									onRemind={(reward) => {
										posthog.capture("referral_reminder_sent", { pct: completionPctOf(reward) });
										sendServerNudge(reward._id);
									}}
								/>
							</div>

							<aside className="lg:w-1/3 space-y-6">
								<PlanPanel payments={payments} summary={summary} isLoading={isLoadingPayments} />
								<div className="hidden lg:block">
									<EarnCards resumePct={resumePct} onBuildResume={goToResume} onRefer={openReferModal} />
								</div>
							</aside>
						</div>
					)}
				</div>
			</div>
		</>
	);
}

export default Rewards;
